import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import sk from './locales/sk.json';
import en from './locales/en.json';

export const SUPPORTED_LANGUAGES = ['sk', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];
const STORAGE_KEY = 'ssi.language';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

function readStoredLanguage(): Language | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storeLanguage(lang: Language): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Storage may be unavailable (private mode, blocked site data) – the choice then lasts for this page only.
  }
}

/** Saved choice → browser language → Slovak. */
export function detectLanguage(): Language {
  const stored = readStoredLanguage();
  if (stored) return stored;
  const browser = typeof navigator !== 'undefined' ? (navigator.languages ?? [navigator.language]) : [];
  for (const tag of browser) {
    const base = tag?.toLowerCase().split('-')[0];
    if (isLanguage(base)) return base;
  }
  return 'sk';
}

export function currentLanguage(): Language {
  return isLanguage(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'sk';
}

void i18n.use(initReactI18next).init({
  resources: { sk: { translation: sk }, en: { translation: en } },
  lng: detectLanguage(),
  fallbackLng: 'sk',
  supportedLngs: [...SUPPORTED_LANGUAGES],
  interpolation: { escapeValue: false },
  returnNull: false,
  react: { useSuspense: false },
});

function syncDocumentLanguage(lang: string) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
}

syncDocumentLanguage(currentLanguage());
i18n.on('languageChanged', (lang) => syncDocumentLanguage(lang));

export async function changeLanguage(lang: Language): Promise<void> {
  storeLanguage(lang);
  await i18n.changeLanguage(lang);
}

export default i18n;

/** Language names in their own language (never translated, shown with a matching lang attribute). */
export const LANGUAGE_AUTONYMS = { sk: 'Slovenčina', en: 'English' } as const;
